import {
  siAnthropic,
  siArxiv,
  siBrave,
  siCloudflare,
  siFigma,
  siGithub,
  siHuggingface,
  siLinear,
  siModelcontextprotocol,
  siNotion,
  siNpm,
  siPypi,
  siShopify,
  siStripe,
  siUpstash,
  siWikipedia,
  siYcombinator,
  type SimpleIcon,
} from 'simple-icons';
import { BadgeDollarSign, BookOpen, CloudSun, Drama, Flame, Globe, Search } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Logos shipped with the console (Simple Icons, CC0), keyed by slug. Nothing is fetched. */
const LOGOS: Record<string, SimpleIcon> = Object.fromEntries(
  [
    siAnthropic,
    siArxiv,
    siBrave,
    siCloudflare,
    siFigma,
    siGithub,
    siHuggingface,
    siLinear,
    siModelcontextprotocol,
    siNotion,
    siNpm,
    siPypi,
    siShopify,
    siStripe,
    siUpstash,
    siWikipedia,
    siYcombinator,
  ].map((icon) => [icon.slug, icon]),
);

/** Brands Simple Icons no longer ships; the path is from its last release that included them. */
const EXTRA_LOGOS: Record<string, Pick<SimpleIcon, 'hex' | 'path'>> = {
  slack: {
    hex: '4A154B',
    path: 'M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z',
  },
};

/** Generic glyphs, drawn white on the brand colour, for entries with no brand logo available. */
const GLYPHS: Record<string, LucideIcon> = {
  currency: BadgeDollarSign,
  deepwiki: BookOpen,
  exa: Search,
  firecrawl: Flame,
  globe: Globe,
  playwright: Drama,
  weather: CloudSun,
};

/** Near-black brand colours (GitHub, Notion…) would vanish in dark mode; use the text colour. */
function isDark(hex: string): boolean {
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b! < 0.25;
}

function initials(name: string): string {
  const words = name.split(/[\s&-]+/).filter(Boolean);
  return (words.length > 1 ? words[0]![0]! + words[1]![0]! : name.slice(0, 2)).toUpperCase();
}

/**
 * A marketplace entry's logo: the brand mark when the console ships one, otherwise a monogram
 * on the brand colour.
 */
export function BrandMark({
  name,
  icon,
  brandColor,
  className,
}: {
  name: string;
  icon: string | null;
  brandColor: string | null;
  className?: string;
}) {
  const logo = icon ? (LOGOS[icon] ?? EXTRA_LOGOS[icon]) : undefined;
  const Glyph = icon ? GLYPHS[icon] : undefined;
  if (logo) {
    return (
      <div
        className={cn(
          'bg-muted flex size-10 shrink-0 items-center justify-center rounded-lg border',
          className,
        )}
      >
        <svg
          viewBox="0 0 24 24"
          aria-hidden
          className="size-5"
          fill={isDark(logo.hex) ? 'currentColor' : `#${logo.hex}`}
        >
          <path d={logo.path} />
        </svg>
      </div>
    );
  }
  return (
    <div
      aria-hidden
      className={cn(
        'flex size-10 shrink-0 items-center justify-center rounded-lg border text-sm font-semibold text-white',
        !brandColor && 'bg-muted text-foreground',
        className,
      )}
      style={brandColor ? { backgroundColor: brandColor } : undefined}
    >
      {Glyph ? <Glyph className="size-5" /> : initials(name)}
    </div>
  );
}
